import { Component, ElementRef, OnDestroy, OnInit, input, output, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DeviceTypeParameterConfigWithAvailableListAndParameter,
  TIMEZONE_ABBREVIATIONS,
} from '@vectronic/util';
import { Subscription, startWith } from 'rxjs';

type Config = DeviceTypeParameterConfigWithAvailableListAndParameter;
type Timezone = { name: string; offset: number };

@Component({
  selector: 'vectronic-parameter-timezone-config',
  templateUrl: './parameter-timezone-config.component.html',
  styleUrls: ['./parameter-timezone-config.component.scss'],
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatIcon,
    MatTooltip,
    MatIconButton,
  ],
})
export class ParameterTimezoneConfigComponent implements OnInit, OnDestroy {
  config = input<Config>();
  value = input<string>();
  disabled = input(false);
  readonly = input<boolean>();
  resetToFactoryTooltip = input<string>();

  hasClose = input(false);
  hideLabel = input<boolean>();
  showInfo = input(true);
  hasDelete = input(true);
  hasUndo = input(false);
  showReset = input<boolean>();

  valueSubscription = toObservable(this.value)
    .pipe(takeUntilDestroyed())
    .subscribe((val) => {
      if (val) {
        this.formControl.setValue(
          this.timezones?.find((timezone) => timezone?.offset === parseInt(val) * 15),
        );
      } else {
        this.formControl.setValue('');
      }
    });

  disabledSubscription = toObservable(this.disabled)
    .pipe(takeUntilDestroyed())
    .subscribe((disabled) => {
      if (disabled) this.formControl.disable();
      else this.formControl.enable();
    });

  parameterUndo = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  resetToFactory = output<DeviceTypeParameterConfigWithAvailableListAndParameter>();
  configClose = output();
  parameterChange = output<{
    config: Config;
    value: string;
  }>();
  timezones: any[] = [];
  formControl = new FormControl('');
  subscription: Subscription;
  filteredOptions: Timezone[] = [];

  timezoneInput = viewChild<ElementRef<HTMLInputElement>>('timezoneInput');
  timezoneAutocomplete = viewChild(MatAutocompleteTrigger);

  ngOnInit() {
    this.timezones = TIMEZONE_ABBREVIATIONS;
    if (this.value()) {
      this.formControl.setValue(
        this.timezones?.find((timezone) => timezone?.offset === parseInt(this.value()) * 15),
      );
    }
    this.subscription = this.formControl.valueChanges.pipe(startWith(null)).subscribe((value) => {
      this.filteredOptions =
        value && this.timezones
          ? this._filter(this.timezones, value)
          : this.timezones?.slice() || [];
    });
  }

  onSelectionChange($event: MatAutocompleteSelectedEvent, config: Config) {
    const value = ($event.option.value?.offset / 15)?.toString();
    this.parameterChange.emit({ config, value });
  }

  private _filter(timezones: any[], value: string): any[] {
    const filterValue = value?.toLowerCase?.()?.trim() || '';
    return timezones.filter(({ name, offset }) => {
      const hours = Math.floor(offset / 60);
      const minutes = Math.abs(offset % 60);
      const sign = hours >= 0 ? '+' : '-';
      const formattedOffset = `${sign}${String(Math.abs(hours)).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      return name?.toLowerCase()?.includes(filterValue) || formattedOffset.includes(filterValue);
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onClickRemove() {
    this.parameterChange.emit({ config: this.config(), value: undefined });
    this.formControl.setValue('');
  }

  selectOption() {
    const newTimezone = this.filteredOptions?.[0];
    if (newTimezone) {
      this.parameterChange.emit({ config: this.config(), value: newTimezone?.offset?.toString() });
      this.formControl.setValue(newTimezone.name);
      if (this.timezoneInput()) {
        this.timezoneInput().nativeElement.value = newTimezone.name;
      }
      if (this.timezoneAutocomplete()) {
        this.timezoneAutocomplete().writeValue(newTimezone);
        this.timezoneAutocomplete().closePanel();
      }
    }
  }

  getTimezoneName(timezone: Timezone) {
    if (!timezone) {
      return '';
    }
    const offset = timezone.offset; // in minutes
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const hours = Math.floor(absOffset / 60);
    const minutes = absOffset % 60;
    const formattedOffset = `${sign}${String(Math.abs(hours)).padStart(2, '0')}${minutes ? ':' + String(minutes).padStart(2, '0') : ''}`;
    return `UTC${formattedOffset} (${timezone.name})`;
  }

  get parameterTitle() {
    const { name, subcategory } = this.config().parameter;
    if (subcategory) return `${subcategory} / ${name}`;
    else return name;
  }

  onClickClose() {
    this.configClose.emit();
  }

  onClickUndo() {
    this.parameterUndo.emit(this.config());
  }

  onResetToFactory() {
    this.resetToFactory.emit(this.config());
  }
}
